import React, { useState, useRef, useEffect } from 'react';
import ReactPlayer from 'react-player';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import VideoControls from './VideoControls';

export default function VideoPlayer({ url, title, onComplete }) {
  const playerRef = useRef(null);
  const [state, setState] = useState({
    playing: false,
    volume: 0.8,
    muted: false,
    played: 0,
    duration: 0,
    playbackRate: 1.0,
    isCompleted: false,
    showControls: true
  });

  const controlsTimeoutRef = useRef(null);

  const togglePlay = () => setState(prev => ({ ...prev, playing: !prev.playing }));
  
  const handleProgress = (progress) => {
    setState(prev => ({ ...prev, played: progress.played }));
    if (progress.played > 0.95 && !state.isCompleted) {
      setState(prev => ({ ...prev, isCompleted: true }));
      onComplete?.();
    }
  };

  const resetControlsTimeout = () => {
    setState(prev => ({ ...prev, showControls: true }));
    if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    controlsTimeoutRef.current = setTimeout(() => {
      if (state.playing) setState(prev => ({ ...prev, showControls: false }));
    }, 3000);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.code === 'Space') { e.preventDefault(); togglePlay(); }
      if (e.code === 'ArrowRight') playerRef.current?.seekTo(playerRef.current.getCurrentTime() + 5);
      if (e.code === 'ArrowLeft') playerRef.current?.seekTo(playerRef.current.getCurrentTime() - 5);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [state.playing]);

  return (
    <div 
      className="relative group bg-black rounded-3xl overflow-hidden aspect-video shadow-2xl border border-slate-800"
      onMouseMove={resetControlsTimeout}
    >
      <ReactPlayer
        ref={playerRef}
        url={url}
        width="100%"
        height="100%"
        playing={state.playing}
        volume={state.volume}
        muted={state.muted}
        playbackRate={state.playbackRate}
        onProgress={handleProgress}
        onDuration={(d) => setState(prev => ({ ...prev, duration: d }))}
        onPause={() => setState(prev => ({ ...prev, playing: false }))}
        onPlay={() => setState(prev => ({ ...prev, playing: true }))}
        progressInterval={100}
        config={{ youtube: { playerVars: { showinfo: 0, rel: 0, modestbranding: 1 } } }}
      />

      {/* Play/Pause Large Overlay */}
      <AnimatePresence>
        {!state.playing && (
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }} 
            animate={{ opacity: 1, scale: 1 }} 
            exit={{ opacity: 0, scale: 0.9 }}
            className="absolute inset-0 flex items-center justify-center bg-black/40 cursor-pointer z-0"
            onClick={togglePlay}
          >
            <div className="w-20 h-20 bg-indigo-600 rounded-full flex items-center justify-center shadow-2xl">
              <SafeIcon icon={FiIcons.FiPlay} className="text-white text-3xl ml-1" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <VideoControls 
        {...state}
        progress={state.played}
        onTogglePlay={togglePlay}
        onSeek={(p) => playerRef.current?.seekTo(p)}
        onVolumeChange={(v) => setState(prev => ({ ...prev, volume: v, muted: v === 0 }))}
        onToggleMute={() => setState(prev => ({ ...prev, muted: !prev.muted }))}
        onRateChange={(r) => setState(prev => ({ ...prev, playbackRate: r }))}
      />
    </div>
  );
}